import { useEffect, useState } from 'react';
import type { InvitePreview } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import { Shell } from '../components/Shell';

interface Props {
  readonly session: StoredSession;
  readonly invite: string;
}

/**
 * The signed-in half of redeeming a share link. Everything before this — the
 * preview, and signing up if you have to — happens on the sign-in page, so by
 * the time anybody gets here the only question left is yes or no.
 */
export const Join = ({ session, invite }: Props) => {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .previewInvite(invite)
      .then((result) => live && setPreview(result))
      .catch((caught: unknown) => {
        if (!live) return;
        setError(caught instanceof Error ? caught.message : 'That link is no longer valid.');
      });
    return () => {
      live = false;
    };
  }, [invite]);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.acceptInvite(session.token, invite);
      go(`/d/${result.deviceId}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell who={session.account.name}>
      <div className="stack-loose" style={{ marginTop: '2.5rem' }}>
        {error ? (
          <div>
            <p className="alert">{error}</p>
            <p style={{ marginTop: '1.5rem' }}>
              <button type="button" className="button button-quiet" onClick={() => go('/')}>
                Go to your frames
              </button>
            </p>
          </div>
        ) : !preview ? (
          <p className="eyebrow">Checking the link</p>
        ) : (
          <>
            <div>
              <p className="eyebrow">You have been invited</p>
              <h1 className="title">{preview.deviceLabel}</h1>
              <p className="lede">
                {preview.invitedBy} wants to share this frame with you
                {preview.role === 'delegate'
                  ? ' and have you help run it: settings, screens, and who else can send.'
                  : '. You will be able to send photos to it, and your name appears under each one.'}
              </p>
            </div>

            <div className="row">
              <button
                type="button"
                className="button"
                disabled={busy}
                onClick={() => void accept()}
              >
                {busy ? 'Joining' : 'Join this frame'}
              </button>
              <button type="button" className="button button-quiet" onClick={() => go('/')}>
                Not now
              </button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
};
